import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { commentKeys } from '../comments/hooks';
import {
  createDm,
  createGroupRoom,
  fetchChatPeople,
  fetchRoomLatestMessages,
  fetchRoomMembers,
  fetchRoomUnread,
  fetchRooms,
  markRoomRead,
} from './rooms';
import { fetchHuddleChat, fetchHuddleChatUnread, markHuddleChatRead } from './huddleChat';
import type { HuddleChatUnread } from './huddleChat';
import { fetchAgentIds } from './agents';
import { applyFeedback, fetchFeedback, saveFeedbackReason } from './feedback';
import type { FeedbackAction } from './feedback';

export const roomKeys = {
  all: ['rooms'] as const,
  list: () => [...roomKeys.all, 'list'] as const,
  latest: (roomIds: string[]) => [...roomKeys.all, 'latest', ...roomIds] as const,
  members: (roomIds: string[]) => [...roomKeys.all, 'members', ...roomIds] as const,
  unread: () => [...roomKeys.all, 'unread'] as const,
  chatPeople: () => [...roomKeys.all, 'chat-people'] as const,
  huddleChat: () => [...roomKeys.all, 'huddle-chat'] as const,
  huddleUnread: () => [...roomKeys.all, 'huddle-unread'] as const,
  agents: () => [...roomKeys.all, 'agents'] as const,
  feedback: (commentIds: string[]) => [...roomKeys.all, 'feedback', ...commentIds] as const,
  feedbackAll: () => [...roomKeys.all, 'feedback'] as const,
};

/**
 * How often unread counts and the open room refresh. A poll, not Realtime:
 * task_comments is not in the supabase_realtime publication (the only table
 * there is tasks, 20260808000100_tasks_realtime.sql:27-35), and the Inbox
 * already made the same call ("No Realtime (locked decision)",
 * notifications/hooks.ts:16-19). 60 seconds, as plan-drop3b.md settled for
 * the no-Realtime case.
 */
export const ROOM_POLL_INTERVAL_MS = 60_000;

/**
 * Keep the unread polls going while the Hub tab is hidden, so the tab title
 * and the desktop alerts (desktopAlerts.ts) learn of a new message in a
 * background tab. React Query skips an interval tick while
 * document.visibilityState is 'hidden' unless this is set (query-core
 * queryObserver #updateRefetchInterval, focusManager.isFocused). Only the two
 * unread counts and the Inbox set it; the open room's thread does not.
 */
export const POLL_IN_BACKGROUND = true;

export function useRooms() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: roomKeys.list(),
    queryFn: () => fetchRooms(client),
    enabled: access.status === 'approved',
    retry: false,
  });
}

export function useRoomLatestMessages(roomIds: string[]) {
  const { client } = useAuth();
  return useQuery({
    queryKey: roomKeys.latest(roomIds),
    queryFn: () => fetchRoomLatestMessages(client, roomIds),
    enabled: roomIds.length > 0,
    refetchInterval: ROOM_POLL_INTERVAL_MS,
    retry: false,
  });
}

/**
 * Members of the given projects: the caller's members-only rooms (DM titles,
 * group lines, the @ picker) and the Alex Huddle threads' projects.
 */
export function useRoomMembers(roomIds: string[]) {
  const { client } = useAuth();
  return useQuery({
    queryKey: roomKeys.members(roomIds),
    queryFn: () => fetchRoomMembers(client, roomIds),
    enabled: roomIds.length > 0,
    retry: false,
  });
}

/**
 * Unread per room. Shared by the app sidebar (TapLayout) and the Messages
 * page through one cache entry, so both show the same number.
 */
export function useRoomUnread(enabled = true) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: roomKeys.unread(),
    queryFn: () => fetchRoomUnread(client),
    enabled: enabled && access.status === 'approved',
    refetchInterval: ROOM_POLL_INTERVAL_MS,
    refetchIntervalInBackground: POLL_IN_BACKGROUND,
    retry: false,
  });
}

/**
 * Reloads one room's thread, and its Drop 3c extras with it: a message's
 * extras row is what tells a forward or a reply apart, and Edit waits for it
 * (MessagesPage actionsFor), so without this a message just sent or just
 * arrived had no Edit until a page reload (Claude live check, 29 Sept).
 * Called by the Messages page when the unread poll says the open room has
 * something new, so the arrival shows and is marked read, and after a send.
 */
export function useRefreshRoomThread() {
  const queryClient = useQueryClient();
  return useCallback(
    (roomId: string) => {
      void queryClient.invalidateQueries({ queryKey: commentKeys.thread(null, roomId) });
      // drop3cKeys.extras(roomId), written out: ./drop3cHooks stays out of
      // this file, which the app shell loads on every page (Shells.tsx).
      // refreshRoomThread.test.tsx checks the two keys still match.
      void queryClient.invalidateQueries({ queryKey: [...roomKeys.all, '3c', 'extras', roomId] });
    },
    [queryClient],
  );
}

export function useMarkRoomRead() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roomId: string) => markRoomRead(client, roomId),
    onSuccess: (_data, roomId) => {
      // Zero the room at once rather than waiting for the next poll.
      queryClient.setQueryData<Map<string, number>>(roomKeys.unread(), (previous) => {
        if (previous === undefined) {
          return previous;
        }
        const next = new Map(previous);
        next.set(roomId, 0);
        return next;
      });
    },
  });
}

export function useChatPeople(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: roomKeys.chatPeople(),
    queryFn: () => fetchChatPeople(client),
    enabled,
    retry: false,
  });
}

export function useCreateDm() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (otherUserId: string) => createDm(client, otherUserId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: roomKeys.all });
    },
  });
}

export function useCreateGroupRoom() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; memberIds: string[] }) => createGroupRoom(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: roomKeys.all });
    },
  });
}

/* ─── Drop 3b-2: the Alex Huddle chat ─── */

/**
 * Whether the Alex Huddle entry shows, and its unread count. Shared by the
 * app sidebar total and the Messages page through one cache entry. Same 60s
 * poll as room unread.
 */
export function useHuddleChatUnread(enabled = true) {
  const { client, access } = useAuth();
  return useQuery<HuddleChatUnread>({
    queryKey: roomKeys.huddleUnread(),
    queryFn: () => fetchHuddleChatUnread(client),
    enabled: enabled && access.status === 'approved',
    refetchInterval: ROOM_POLL_INTERVAL_MS,
    refetchIntervalInBackground: POLL_IN_BACKGROUND,
    retry: false,
  });
}

/** The chat itself, loaded only while the Alex Huddle entry is open. */
export function useHuddleChat(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: roomKeys.huddleChat(),
    queryFn: () => fetchHuddleChat(client),
    enabled,
    retry: false,
  });
}

export function useRefreshHuddleChat() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: roomKeys.huddleChat() });
  }, [queryClient]);
}

export function useMarkHuddleChatRead() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markHuddleChatRead(client),
    onSuccess: () => {
      queryClient.setQueryData<HuddleChatUnread>(roomKeys.huddleUnread(), (previous) =>
        previous === undefined ? previous : { ...previous, unread: 0 },
      );
    },
  });
}

/* ─── Drop 3b-2: thumbs on agent messages ─── */

/** Ids of agent accounts (profiles.is_agent). Rarely changes: read once. */
export function useAgentIds() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: roomKeys.agents(),
    queryFn: () => fetchAgentIds(client),
    enabled: access.status === 'approved',
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Every grade and "What was wrong?" note the database lets this person read on
 * these messages (the room's people, Kyu 26 Sept), with their own marked by
 * user_id. A TAP Viewer reads them too; only grading is refused.
 */
export function useMessageFeedback(commentIds: string[]) {
  const { client, access } = useAuth();
  const myId = access.status === 'approved' ? access.profile.id : '';
  return useQuery({
    queryKey: roomKeys.feedback(commentIds),
    queryFn: () => fetchFeedback(client, commentIds, myId),
    enabled: commentIds.length > 0 && myId !== '',
    retry: false,
  });
}

export function useApplyFeedback() {
  const { client, access } = useAuth();
  const myId = access.status === 'approved' ? access.profile.id : '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { commentId: string; action: FeedbackAction }) =>
      applyFeedback(client, { ...input, myId }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: roomKeys.feedbackAll() });
    },
  });
}

export function useSaveFeedbackReason() {
  const { client, access } = useAuth();
  const myId = access.status === 'approved' ? access.profile.id : '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { commentId: string; reason: string }) =>
      saveFeedbackReason(client, { ...input, myId }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: roomKeys.feedbackAll() });
    },
  });
}
